/// /////////////////////////////////////////////////////////////////////////////////////
///                           Copyright (c) 2020 - 2021
///                            Coyote Logistics L.L.C.
///                          All Rights Reserved Worldwide
///
/// WARNING:  This program (or document) is unpublished, proprietary
/// property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
/// Unauthorized reproduction, distribution or disclosure of this program
/// (or document), or any program (or document) derived from it is
/// prohibited by State and Federal law, and by local law outside of the U.S.
/// //////////////////////////////////////////////////////////////////////////////////////

import React, { useEffect, useState } from "react";

import { Grid, Button } from "@material-ui/core";
import { withTheme } from "@material-ui/core/styles";
import CachedIcon from "@material-ui/icons/Cached";
import RefreshResetFilters from "../components/filters/RefreshResetFilters";
import RouteTypeFilter from "../components/filters/RouteTypeFilter";
import LocationFilter from "../components/filters/LocationFilter";
import AlertsFilter from "../components/filters/AlertsFilter";
import DateRangePicker from "../components/date-picker/DateRangePicker";
import { updateFilters } from "../slices/filtersSlice";
import { fetchServiceLevels } from "../slices/serviceLevelsSlice";
import { fetchCarriers } from "../slices/carriersSlice";

import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { format, parse } from "date-fns";
import { useHistory } from "react-router-dom";
import useViewport from "../helpers/useViewport";
import defaultGoTrackFilters from "../helpers/defaultGoTrackFilters";
import types from "../helpers/types";
import CarrierMultiFilter from "../components/filters/CarrierMultiFilter";
import ServiceLevelMultiFilter from "../components/filters/ServiceLevelMultiFilter";
import RegionMultiFilter from "../components/filters/RegionMultiFilter";
import DistrictMultiFilter from "../components/filters/DistrictMultiFilter";
import SortMultiFilter from "../components/filters/SortMultiFilter";

const StyledWrapperGridItem = styled(Grid)`
  background-color: ${(props) => props.theme.palette.background.lightGrey};
  flex: 0 0 auto !important;
  padding: 16px;

  .MuiFormControl-marginDense {
    margin-bottom: 0;
    margin-top: 0;
  }
  .MuiAutocomplete-marginDense {
    margin-bottom: 0;
    margin-top: 0;
  }
  .MuiAutocomplete-inputRoot
  .MuiAutocomplete-input {
    min-width: 0px !important;
  }  

  .MuiAutocomplete-tag {
    text-overflow: ellipsis !important;
    white-space: nowrap !important;
    overflow: hidden !important;
  }
`;

const StyledFilterGridItem = styled(Grid)`
  padding-right: 0 !important;
  padding-left: 8px !important;
  padding-bottom: 4px !important;
`;

const StyledIconButton = styled(Button)`
  min-width: 32px !important;
  .MuiButton-startIcon {
    margin: 0px !important;
  }
`;

const FiltersContainer = ({ theme }) => {
  //using local state and redux state here to keep the date picker in sync,
  //and to only update the URL params when the full date range is selected
  const filters = useSelector((state) => state.filters);
  const { startDate, endDate, carrierId } = filters;
  const [startDateLocal, setStartDateLocal] = useState(null);
  const [endDateLocal, setEndDateLocal] = useState(null);
  const [lastUpdateDate, setLastUpdateDate] = useState(new Date());
  const [carrierIdLocal, setCarrierIdLocal] = useState(null);
  const [dateRangePlaceHolderMessage, setDateRangePlaceHolderMessage] = useState("Departure Date Range");
  const [regionPlaceHolderMessage, setRegionPlaceHolderMessage] = useState("Origin Region");
  const [districtPlaceHolderMessage, setDistrictPlaceHolderMessage] = useState("Origin District");
  const [locationPlaceHolderMessage, setLocationPlaceHolderMessage] = useState("Origin Location");
  const [sortPlaceHolderMessage, setSortPlaceHolderMessage] = useState("Origin Sort");

  const history = useHistory();
  const dispatch = useDispatch();
  const screenWidth = useViewport();
  const filtersBreakpoint = 1680;

  const routeType = useSelector((state) => {
    return state.filters.routeType;
  });

  const handleDateSelect = (startDate, endDate) => {
    setStartDateLocal(startDate);
    setEndDateLocal(endDate);
    if (startDate && endDate) {
      dispatch(
        updateFilters({
          filters: {
            startDate: startDate
              ? format(startDate, "MM/dd/yyyy")
              : startDateLocal,
            endDate: endDate ? format(endDate, "MM/dd/yyyy") : endDateLocal,
          },
          history,
        })
      );
    } else if (!startDate && !endDate) {
      dispatch(
        updateFilters({
          filters: {
            startDate: null,
            endDate: null,
          },
          history,
        })
      );
    }
  };

  const handleCarrierSelect = (carrierId) => {
    dispatch(
      updateFilters({
        filters: { carrierId },
        history,
      })
    );
  };

  const handleFilterRefresh = () => {
    dispatch(
      updateFilters({
        filters: {
          ...filters,
        },
        history,
      })
    );
  };

  const handleFilterReset = () => {
    setCarrierIdLocal(null);

    dispatch(
      updateFilters({
        filters: {
          ...defaultGoTrackFilters,
        },
        history,
      })
    );
  };

  useEffect(() => {
    if (startDate && endDate) {
      setStartDateLocal(parse(startDate, "MM/dd/yyyy", new Date()));
      setEndDateLocal(parse(endDate, "MM/dd/yyyy", new Date()));
    }
  }, [startDate, endDate]);

  useEffect(() => {
    dispatch(fetchServiceLevels());
    dispatch(fetchCarriers());
  }, [dispatch]);

  useEffect(() => {
    setLastUpdateDate(new Date());
  }, [filters]);

  useEffect(() => {
    setCarrierIdLocal(carrierId);
  }, [carrierId]);

   // When the route type changes we want to change the input placeholders
   useEffect(() => {
    if(routeType == types.routeTypes.active){
      setDateRangePlaceHolderMessage("Arrival Date Range");
      setRegionPlaceHolderMessage("Dest. Region");
      setDistrictPlaceHolderMessage("Dest. District");
      setLocationPlaceHolderMessage("Dest. Location");
      setSortPlaceHolderMessage("Dest. Sort");
    }
    else{
      setDateRangePlaceHolderMessage("Departure Date Range");
      setRegionPlaceHolderMessage("Origin Region");
      setDistrictPlaceHolderMessage("Origin District");
      setLocationPlaceHolderMessage("Origin Location");
      setSortPlaceHolderMessage("Origin Sort");
    }
  }, [routeType]);

  return (
    <StyledWrapperGridItem item theme={theme}>
      <Grid container spacing={4} alignItems="center">
        <StyledFilterGridItem item>
          <RouteTypeFilter />
        </StyledFilterGridItem>
        <StyledFilterGridItem item>
          <DateRangePicker
            placeholder={dateRangePlaceHolderMessage}
            handleDateSelect={handleDateSelect}
            startDate={startDateLocal}
            endDate={endDateLocal}
            inputLabel={dateRangePlaceHolderMessage}
            inputLabelWidth={140}
          />
        </StyledFilterGridItem>
        <StyledFilterGridItem item>
          <CarrierMultiFilter />
        </StyledFilterGridItem>
        <StyledFilterGridItem item>
          <ServiceLevelMultiFilter />
        </StyledFilterGridItem>
        <StyledFilterGridItem item>
          <RegionMultiFilter placeholder={regionPlaceHolderMessage} />
        </StyledFilterGridItem>
        <StyledFilterGridItem item>
          <DistrictMultiFilter placeholder={districtPlaceHolderMessage} />
        </StyledFilterGridItem>
        <StyledFilterGridItem item>
          <LocationFilter placeholder={locationPlaceHolderMessage} />
        </StyledFilterGridItem>
        <StyledFilterGridItem item>
          <SortMultiFilter placeholder={sortPlaceHolderMessage} />
        </StyledFilterGridItem>
        <StyledFilterGridItem item>
          <AlertsFilter />
        </StyledFilterGridItem>
        {screenWidth < filtersBreakpoint ? (
          <StyledFilterGridItem item xs={12} style={{ paddingTop: 0 }}>
            <Grid container>
              <RefreshResetFilters
                isGoTrack={true}
                handleReset={handleFilterReset}
                lastUpdateDate={lastUpdateDate}
                filtersBreakpoint={filtersBreakpoint}
              >
                <StyledIconButton
                  variant="contained"
                  onClick={handleFilterRefresh}
                  startIcon={<CachedIcon />}
                  data-testid="refresh-button"
                  size="small"
                />
              </RefreshResetFilters>
            </Grid>
          </StyledFilterGridItem>
        ) : (
          <StyledFilterGridItem item>
            <Grid container spacing={1} alignItems="center">
              <Grid item>
                <StyledIconButton
                  variant="contained"
                  onClick={handleFilterRefresh}
                  startIcon={<CachedIcon />}
                  data-testid="refresh-button"
                ></StyledIconButton>
              </Grid>
              <Grid item>
                <RefreshResetFilters
                  handleReset={handleFilterReset}
                  lastUpdateDate={lastUpdateDate}
                  isGoTrack={true}
                  filtersBreakpoint={filtersBreakpoint}
                />
              </Grid>
            </Grid>
          </StyledFilterGridItem>
        )}
      </Grid>
    </StyledWrapperGridItem>
  );
};

export default withTheme(FiltersContainer);
